import { useEffect } from 'react';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Button,
  Grid2 as Grid,
  Typography,
  Divider,
  Box,
  Alert,
} from '@mui/material';
import { personSchema } from '../../schemas/validationSchemas';

/**
 * Formulário de Pessoa (Create/Update) com validação Zod
 */
export default function PersonForm({ open, onClose, onSubmit, data, loading = false }) {
  const isEdit = !!data;

  const {
    register,
    handleSubmit,
    reset,
    formState: { errors, isValid, isDirty },
  } = useForm({
    resolver: zodResolver(personSchema),
    mode: 'onChange',
    defaultValues: {
      name: '',
      email: '',
      phone: '',
      cpf: '',
      birthDate: '',
      address: '',
      notes: '',
    },
  });

  useEffect(() => {
    if (open) {
      if (data) {
        reset({
          name: data.name || '',
          email: data.email || '',
          phone: data.phone || '',
          cpf: data.cpf || '',
          birthDate: data.birthDate ? data.birthDate.split('T')[0] : '',
          address: data.address || '',
          notes: data.notes || '',
        });
      } else {
        reset({
          name: '',
          email: '',
          phone: '',
          cpf: '',
          birthDate: '',
          address: '',
          notes: '',
        });
      }
    }
  }, [data, open, reset]);

  const onSubmitForm = (formData) => {
    onSubmit(formData);
  };

  const handleClose = () => {
    reset();
    onClose();
  };

  return (
    <Dialog open={open} onClose={handleClose} maxWidth="md" fullWidth>
      <DialogTitle>
        {isEdit ? 'Editar Pessoa' : 'Nova Pessoa'}
      </DialogTitle>

      <form onSubmit={handleSubmit(onSubmitForm)}>
        <DialogContent>
          <Box display="flex" flexDirection="column" gap={2.5}>
            {/* Dados Pessoais */}
            <Box>
              <Typography variant="subtitle2" color="primary" gutterBottom fontWeight="bold">
                Dados Pessoais
              </Typography>
              <Grid container spacing={2}>
                <Grid size={{ xs: 12 }}>
                  <TextField
                    autoFocus
                    label="Nome Completo"
                    fullWidth
                    {...register('name')}
                    error={!!errors.name}
                    helperText={errors.name?.message}
                    required
                    disabled={loading}
                  />
                </Grid>
                <Grid size={{ xs: 12, sm: 6 }}>
                  <TextField
                    label="Email"
                    type="email"
                    fullWidth
                    {...register('email')}
                    error={!!errors.email}
                    helperText={errors.email?.message}
                    disabled={loading}
                  />
                </Grid>
                <Grid size={{ xs: 12, sm: 6 }}>
                  <TextField
                    label="Telefone"
                    fullWidth
                    {...register('phone')}
                    error={!!errors.phone}
                    helperText={errors.phone?.message || '(11) 98765-4321'}
                    disabled={loading}
                  />
                </Grid>
                <Grid size={{ xs: 12, sm: 6 }}>
                  <TextField
                    label="CPF"
                    fullWidth
                    {...register('cpf')}
                    error={!!errors.cpf}
                    helperText={errors.cpf?.message || 'Somente números ou XXX.XXX.XXX-XX'}
                    disabled={loading}
                  />
                </Grid>
                <Grid size={{ xs: 12, sm: 6 }}>
                  <TextField
                    label="Data de Nascimento"
                    type="date"
                    fullWidth
                    {...register('birthDate')}
                    error={!!errors.birthDate}
                    helperText={errors.birthDate?.message}
                    disabled={loading}
                    InputLabelProps={{ shrink: true }}
                  />
                </Grid>
              </Grid>
            </Box>

            <Divider />

            {/* Endereço */}
            <Box>
              <Typography variant="subtitle2" color="primary" gutterBottom fontWeight="bold">
                Endereço
              </Typography>
              <Grid container spacing={2}>
                <Grid size={{ xs: 12 }}>
                  <TextField
                    label="Endereço Completo"
                    fullWidth
                    multiline
                    rows={2}
                    {...register('address')}
                    error={!!errors.address}
                    helperText={errors.address?.message || 'Rua, número, bairro, cidade, estado'}
                    disabled={loading}
                  />
                </Grid>
              </Grid>
            </Box>

            <Divider />

            {/* Observações */}
            <Box>
              <Typography variant="subtitle2" color="primary" gutterBottom fontWeight="bold">
                Observações
              </Typography>
              <TextField
                label="Notas Adicionais"
                fullWidth
                multiline
                rows={3}
                {...register('notes')}
                error={!!errors.notes}
                helperText={errors.notes?.message || 'Informações relevantes sobre a pessoa'}
                disabled={loading}
              />
            </Box>

            {/* Alert de validação */}
            {Object.keys(errors).length > 0 && (
              <Alert severity="error">
                Por favor, corrija os erros acima antes de salvar.
              </Alert>
            )}
          </Box>
        </DialogContent>

        <DialogActions>
          <Button onClick={handleClose} disabled={loading}>Cancelar</Button>
          <Button type="submit" variant="contained" disabled={loading || !isValid || (!isDirty && !isEdit)}>
            {loading ? 'Salvando...' : 'Salvar'}
          </Button>
        </DialogActions>
      </form>
    </Dialog>
  );
}
